"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ReadError,
  readDecimalNumber,
} from "@/lib/tool-kit/numbers-languages/number-input";
import {
  type DecimalMark,
  MAX_ORDINAL,
  type OrdinalGender,
  type OrdinalSpelling,
  type SpanishNote,
  type SpanishNoun,
  spanishNumber,
  spanishOrdinal,
} from "@/lib/tool-kit/numbers-languages/spanish";

/** English strings. A translated content file overrides these through `ui`. */
export const SPANISH_NUMBER_CONVERTER_STRINGS = {
  numberLabel: "Number",
  numberHint:
    "Whole numbers up to 24 digits; a decimal point or comma, a minus sign and spaces between digit groups are fine.",
  numberPlaceholder: "2026",
  modeLabel: "Spell it as",
  modeCardinal: "Cardinal (uno, dos, tres)",
  modeOrdinal: "Ordinal (primero, segundo)",
  nounLabel: "Noun it counts",
  nounNone: "None — just counting (uno)",
  nounMasculine: "Masculine noun (un libro)",
  nounFeminine: "Feminine noun (una casa)",
  markLabel: "Decimal mark, read aloud as",
  markComa: "coma (RAE, Spain)",
  markPunto: "punto (much of Latin America)",
  fullFeminine:
    "Write “veintiuna mil” (full form) before mil with a feminine noun",
  genderLabel: "Gender",
  genderMasculine: "Masculine (primero)",
  genderFeminine: "Feminine (primera)",
  beforeNoun: "Before a masculine singular noun (primer, tercer)",
  spellingLabel: "Spelling of 13–19 and 21–29",
  spellingOneWord: "One word (decimotercero)",
  spellingTwoWords: "Two words (décimo tercero)",
  resultHeading: "In Spanish",
  copy: "Copy words",
  copied: "Copied!",
  abbreviationLabel: "Abbreviation",
  alsoCorrect: "Also correct",
  beforeNounLine: "Before a noun: {words} de …",
  rulesHeading: "Why it is spelled this way",
  enterPrompt: "Type a number to see it spelled out.",
  errorInvalid:
    "That is not a number. Use digits, with an optional minus sign and decimal point.",
  errorTooBig: "Numbers are limited to 24 digits (below 10²⁴).",
  errorTooLong: "Keep the decimal part to 20 digits or fewer.",
  errorOrdinalRange:
    "Ordinals are supported for whole numbers from 1 to {max}.",
  noteApocope:
    "Before a masculine noun a final “uno” shortens to “un”: veintiún, treinta y un, ciento un.",
  noteApocopeBeforeMil:
    "Before “mil” and before “millón/millones” the “uno” is shortened too: veintiún mil, treinta y un millones.",
  noteCien:
    "“Cien” is the form on its own, before a noun, and before “mil” or “millones”.",
  noteCiento: "“Ciento” is used when more words follow it: ciento uno.",
  noteFeminineHundreds:
    "From 200 to 900 the hundreds agree with a feminine noun (doscientas) and keep agreeing across “mil”.",
  noteDe:
    "A noun after a round million takes “de”: “{words} de personas”. Without “de” when other numbers follow: un millón doscientas mil personas.",
  noteLongScale:
    "Spanish uses the long scale: “mil millones” is 10⁹ and “un billón” is 10¹², not the US billion.",
} as const;

const DEFAULT_NUMBER = "2026";

type Strings = typeof SPANISH_NUMBER_CONVERTER_STRINGS;
type Mode = "cardinal" | "ordinal";

const NOTE_KEYS: Record<SpanishNote, keyof Strings> = {
  apocope: "noteApocope",
  apocopeBeforeMil: "noteApocopeBeforeMil",
  cien: "noteCien",
  ciento: "noteCiento",
  feminineHundreds: "noteFeminineHundreds",
  de: "noteDe",
  longScale: "noteLongScale",
};

interface ResultView {
  words: string;
  abbreviation?: string;
  alternatives: string[];
  notes: string[];
  beforeNoun?: string;
}

interface Controls {
  mode: Mode;
  noun: SpanishNoun;
  mark: DecimalMark;
  fullFeminine: boolean;
  gender: OrdinalGender;
  ordinalBeforeNoun: boolean;
  spelling: OrdinalSpelling;
}

type Outcome = { view: ResultView } | { error: string } | null;

function errorText(error: ReadError, strings: Strings): string | null {
  switch (error) {
    case "empty":
      return null;
    case "tooBig":
      return strings.errorTooBig;
    case "tooLong":
      return strings.errorTooLong;
    default:
      return strings.errorInvalid;
  }
}

function cardinalView(
  value: { negative: boolean; integer: string; fraction: string },
  controls: Controls,
  strings: Strings
): ResultView {
  const result = spanishNumber(value.negative, value.integer, value.fraction, {
    noun: controls.noun,
    femBeforeMil: controls.fullFeminine ? "full" : "apocope",
    mark: controls.mark,
  });
  return {
    words: result.words,
    alternatives: result.alternatives,
    notes: result.notes.map((note) =>
      t(strings[NOTE_KEYS[note]], { words: result.words })
    ),
    beforeNoun: result.needsDe
      ? t(strings.beforeNounLine, { words: result.words })
      : undefined,
  };
}

function ordinalOutcome(
  value: { negative: boolean; integer: string; fraction: string },
  controls: Controls,
  strings: Strings
): Outcome {
  const n = Number(value.integer);
  const ordinal =
    value.negative ||
    value.fraction !== "" ||
    Number(value.integer) > MAX_ORDINAL
      ? null
      : spanishOrdinal(n, {
          gender: controls.gender,
          beforeNoun: controls.ordinalBeforeNoun,
          spelling: controls.spelling,
        });
  if (ordinal === null) {
    return {
      error: t(strings.errorOrdinalRange, { max: String(MAX_ORDINAL) }),
    };
  }
  return {
    view: {
      words: ordinal.words,
      abbreviation: ordinal.abbreviation,
      alternatives: ordinal.alternatives,
      notes: [],
    },
  };
}

function evaluate(raw: string, controls: Controls, strings: Strings): Outcome {
  const read = readDecimalNumber(raw);
  if (!read.ok) {
    const message = errorText(read.error, strings);
    return message === null ? null : { error: message };
  }
  if (controls.mode === "ordinal") {
    return ordinalOutcome(read.value, controls, strings);
  }
  return { view: cardinalView(read.value, controls, strings) };
}

function ResultPanel({
  view,
  strings,
}: {
  view: ResultView;
  strings: Strings;
}) {
  return (
    <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.resultHeading}</h2>
      <p
        className="break-words font-semibold text-2xl text-foreground"
        lang="es"
      >
        {view.words}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={view.words}
        />
        {view.abbreviation ? (
          <span className="text-foreground text-sm">
            {strings.abbreviationLabel}:{" "}
            <span className="font-mono">{view.abbreviation}</span>
          </span>
        ) : null}
      </div>
      {view.beforeNoun ? (
        <p className="text-foreground text-sm" lang="es">
          {view.beforeNoun}
        </p>
      ) : null}
      {view.alternatives.length > 0 ? (
        <p className="text-foreground text-sm">
          {strings.alsoCorrect}:{" "}
          <span lang="es">{view.alternatives.join(" · ")}</span>
        </p>
      ) : null}
      {view.notes.length > 0 ? (
        <div className="space-y-2">
          <h3 className="font-medium text-foreground text-sm">
            {strings.rulesHeading}
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm leading-relaxed">
            {view.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default function SpanishNumberConverterWidget() {
  const strings = useToolUi(SPANISH_NUMBER_CONVERTER_STRINGS);
  const [raw, setRaw] = useState(DEFAULT_NUMBER);
  const [controls, setControls] = useState<Controls>({
    mode: "cardinal",
    noun: "none",
    mark: "coma",
    fullFeminine: false,
    gender: "masculine",
    ordinalBeforeNoun: false,
    spelling: "oneWord",
  });
  const set = <K extends keyof Controls>(key: K, value: Controls[K]) =>
    setControls((current) => ({ ...current, [key]: value }));
  const outcome = evaluate(raw, controls, strings);

  return (
    <div className="space-y-6">
      <TextField
        hint={strings.numberHint}
        id="sp-number"
        label={strings.numberLabel}
        numeric
        onChange={setRaw}
        placeholder={strings.numberPlaceholder}
        value={raw}
      />
      <PillGroup
        label={strings.modeLabel}
        name="sp-mode"
        onChange={(value) => set("mode", value)}
        options={[
          { value: "cardinal", label: strings.modeCardinal },
          { value: "ordinal", label: strings.modeOrdinal },
        ]}
        value={controls.mode}
      />
      {controls.mode === "cardinal" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="sp-noun"
            label={strings.nounLabel}
            onChange={(value) => set("noun", value)}
            options={[
              { value: "none", label: strings.nounNone },
              { value: "masculine", label: strings.nounMasculine },
              { value: "feminine", label: strings.nounFeminine },
            ]}
            value={controls.noun}
          />
          <SelectField
            id="sp-mark"
            label={strings.markLabel}
            onChange={(value) => set("mark", value)}
            options={[
              { value: "coma", label: strings.markComa },
              { value: "punto", label: strings.markPunto },
            ]}
            value={controls.mark}
          />
          {controls.noun === "feminine" ? (
            <Checkbox
              checked={controls.fullFeminine}
              label={strings.fullFeminine}
              onChange={(value) => set("fullFeminine", value)}
            />
          ) : null}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.genderLabel}
            name="sp-gender"
            onChange={(value) => set("gender", value)}
            options={[
              { value: "masculine", label: strings.genderMasculine },
              { value: "feminine", label: strings.genderFeminine },
            ]}
            value={controls.gender}
          />
          <SelectField
            id="sp-spelling"
            label={strings.spellingLabel}
            onChange={(value) => set("spelling", value)}
            options={[
              { value: "oneWord", label: strings.spellingOneWord },
              { value: "twoWords", label: strings.spellingTwoWords },
            ]}
            value={controls.spelling}
          />
          {controls.gender === "masculine" ? (
            <Checkbox
              checked={controls.ordinalBeforeNoun}
              label={strings.beforeNoun}
              onChange={(value) => set("ordinalBeforeNoun", value)}
            />
          ) : null}
        </div>
      )}
      {outcome === null ? <Notice>{strings.enterPrompt}</Notice> : null}
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "view" in outcome ? (
        <ResultPanel strings={strings} view={outcome.view} />
      ) : null}
    </div>
  );
}
